import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Exercises } from "../components/Exercises";
import { SessionInfoCard } from "../components/SessionInfoCard";
import { KotCoachStartRoundSlot } from "../components/KotCoachStartRoundSlot";
import { SessionReadOnlyBanner } from "../components/SessionReadOnlyBanner";
import { updateTrainingSessionGrid } from "../components/funtionsBD/Mainservice";
import { usePlanningAccess } from "../hooks/usePlanningAccess";
import { initializeSocket } from "../socket";
import { useTourDemoActive } from "../tour/useTourDemoActive";
import { demoHomeGroups, demoPlanningLanes, demoSession } from "../tour/tourDemoFixtures";
import { PlanningSubgroupBoard } from "../components/planning/PlanningSubgroupBoard";
import "./Planning.css";

export function Planning() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSession, setSelectedSession] = useState(() => {
    const fromState = location.state?.session;
    if (fromState?.ts_id) {
      try {
        localStorage.setItem("rsessn", JSON.stringify(fromState));
      } catch (e) {}
      return fromState;
    }
    return JSON.parse(localStorage.getItem("rsessn"));
  });
  const [selectedgrp, setSelectedgrp] = useState(
    JSON.parse(localStorage.getItem("grpinf"))
  );
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("exercises");
  const { t } = useTranslation();
  const tourDemo = useTourDemoActive();
  const displaySession = selectedSession || (tourDemo ? demoSession() : null);
  const displayGrp = selectedgrp || (tourDemo ? demoHomeGroups()[0] : selectedgrp);
  const {
    canEditPlanning: coachPlanning,
    isSessionReadOnly,
  } = usePlanningAccess();

  const exercisesRef = useRef(null);
  const [planningExerciseDurationsDirty, setPlanningExerciseDurationsDirty] =
    useState(false);
  const [planningExerciseTotalDuration, setPlanningExerciseTotalDuration] =
    useState(null);
  const [coachControlUseContinue, setCoachControlUseContinue] = useState(false);
  const [kotStartRoundCtx, setKotStartRoundCtx] = useState(null);

  const token = localStorage.getItem("token");
  const planningSocket = useMemo(() => initializeSocket(token), [token]);

  const handleCoachExerciseControlContext = useCallback((ctx) => {
    setCoachControlUseContinue(!!ctx?.useContinueLabel);
  }, []);

  const handleKotStartRoundContext = useCallback((ctx) => {
    setKotStartRoundCtx(ctx);
  }, []);

  const handleSessionMetaFromSocket = useCallback((patch) => {
    if (!patch?.ts_id) return;
    setSelectedSession((prev) => {
      if (!prev || prev.ts_id !== patch.ts_id) return prev;
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem("rsessn", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const handleApplySessionMeta = async ({ date, time, duration }) => {
    if (!selectedSession?.ts_id) return;
    const payload = {
      ts_id: selectedSession.ts_id,
      ts_date: moment(date).format("DD/MM/YYYY"),
      ts_start_time: moment(time, "HH:mm").format("h:mm A"),
      ts_duration: duration,
    };
    await updateTrainingSessionGrid(payload);
    setSelectedSession((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        ts_date: date,
        ts_start_time: `${time}:00`,
        ts_duration: duration,
      };
      try {
        localStorage.setItem("rsessn", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  };

  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
    localStorage.setItem("fltrop", filter);
  };

  // Listen for session selection events and localStorage changes
  useEffect(() => {
    const handleSessionSelected = () => {
      try {
        const storedSessionStr = localStorage.getItem("rsessn");
        if (!storedSessionStr) {
          if (selectedSession !== null) {
            setSelectedSession(null);
          }
          return;
        }

        const storedSession = JSON.parse(storedSessionStr);
        if (storedSession && storedSession.ts_id) {
          const sessionGrId = storedSession.gr_id || storedSession._groupId;
          const groupGrId = selectedgrp?.groupDetails?.gr_id;
          if (!groupGrId || !sessionGrId || sessionGrId === groupGrId) {
            if (!selectedSession || selectedSession.ts_id !== storedSession.ts_id) {
              setSelectedSession(storedSession);
            }
          }
        }
      } catch (error) {
        console.error("Error parsing session from localStorage:", error);
      }
    };

    const handleGroupSelected = () => {
      try {
        const storedGroupStr = localStorage.getItem("grpinf");
        if (storedGroupStr) {
          const storedGroup = JSON.parse(storedGroupStr);
          if (storedGroup && storedGroup.groupDetails) {
            setSelectedgrp(storedGroup);
          }
        }
      } catch (error) {
        console.error("Error parsing group from localStorage:", error);
      }
    };

    window.addEventListener("sessionSelected", handleSessionSelected);
    window.addEventListener("storage", handleSessionSelected);
    window.addEventListener("groupSelected", handleGroupSelected);

    handleSessionSelected();
    handleGroupSelected();

    return () => {
      window.removeEventListener("sessionSelected", handleSessionSelected);
      window.removeEventListener("storage", handleSessionSelected);
      window.removeEventListener("groupSelected", handleGroupSelected);
    };
  }, [selectedgrp?.groupDetails?.gr_id, selectedSession?.ts_id]);

  return (
    <div
      id="adminRoot"
      className="coach-session-page planning-page planning-page--stations"
      style={{
        minHeight: "100vh",
      }}
    >
      {isSessionReadOnly ? <SessionReadOnlyBanner page="planning" /> : null}
      <div
        className="planning-unified"
        data-tour-id="coach-planning-board"
      >
        <SessionInfoCard
          variant="planning"
          selectedSession={displaySession}
          selectedgrp={displayGrp}
          allowEditSessionMeta={Boolean(coachPlanning && selectedSession?.ts_id)}
          onApplySessionMeta={
            coachPlanning && selectedSession?.ts_id
              ? handleApplySessionMeta
              : undefined
          }
          planningExerciseDurationsDirty={
            coachPlanning ? planningExerciseDurationsDirty : false
          }
          planningExerciseTotalMinutes={
            coachPlanning ? planningExerciseTotalDuration : null
          }
          onApplyPlanningExerciseDurations={
            coachPlanning
              ? async () => {
                  await exercisesRef.current?.applyModifiedPlanningDurations?.();
                }
              : undefined
          }
          exerciseControlUseContinue={coachControlUseContinue}
          kotStartRoundSlot={
            kotStartRoundCtx?.teId && selectedSession?.ts_id ? (
              <KotCoachStartRoundSlot
                buttonOnly
                socket={planningSocket}
                teId={kotStartRoundCtx.teId}
                tsId={selectedSession.ts_id}
                exerciseStateId={kotStartRoundCtx.exerciseStateId}
              />
            ) : null
          }
          onExerciseControlStart={
            selectedSession?.ts_id
              ? () => {
                  const ix =
                    exercisesRef.current?.getCoachExerciseControlTargetIndex?.() ??
                    0;
                  navigate("/CoachExerciseControl", {
                    state: {
                      session: selectedSession,
                      selectedgrp,
                      alignCoachControlIndex: true,
                      initialExerciseIndex: ix,
                    },
                  });
                }
              : undefined
          }
        />
        {selectedSession ? (
          <Exercises
            ref={exercisesRef}
            selectedSession={selectedSession}
            setSelectedExercise={setSelectedExercise}
            selectedExercise={selectedExercise}
            handleFilterClick={handleFilterClick}
            selectedgrp={selectedgrp}
            onSessionMetaUpdate={handleSessionMetaFromSocket}
            onPlanningDurationDirtyChange={
              coachPlanning ? setPlanningExerciseDurationsDirty : undefined
            }
            onPlanningTotalDurationChange={
              coachPlanning ? setPlanningExerciseTotalDuration : undefined
            }
            onCoachExerciseControlContext={handleCoachExerciseControlContext}
            onKotStartRoundContext={handleKotStartRoundContext}
          />
        ) : tourDemo ? (
          <PlanningSubgroupBoard
            lanes={demoPlanningLanes()}
            canEdit={false}
            highlightActions
          />
        ) : (
          <div className="planning-empty" role="status">
            {t("noSessionSelected") ||
              "Please select a training session from the Home page to view exercises"}
          </div>
        )}
      </div>
    </div>
  );
}
